import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, CheckCircle, Sparkles } from 'lucide-react';
import { carBrands, carModels, bangaloreLocations } from '../mock/mockData';
import { toast } from '../hooks/use-toast';
import { bookingService } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls = "bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0 focus-visible:ring-offset-0";
const selectTriggerCls = "bg-slate-950/60 border-slate-700 text-white hover:border-cyan-500/40 focus:ring-0 focus:ring-offset-0";
const selectContentCls = "bg-slate-900 border-slate-700";
const selectItemCls = "text-slate-200 focus:bg-slate-800 focus:text-white";
const labelCls = "block text-xs uppercase tracking-wider text-slate-400 mb-2";

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { service, package: selectedPackage, carBrand, carModel } = location.state || {};

  const [formData, setFormData] = useState({
    name: '', email: '', mobile: '',
    carBrand: carBrand || '', carModel: carModel || '',
    fuelType: '', manufacturingYear: '',
    address: '', location: '', date: null, timeSlot: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableBrands, setAvailableBrands] = useState(carBrands);
  const [availableModels, setAvailableModels] = useState([]);

  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  const allTimeSlots = ['09:00 AM - 11:00 AM', '11:00 AM - 01:00 PM', '01:00 PM - 03:00 PM', '03:00 PM - 05:00 PM', '05:00 PM - 07:00 PM'];

  const getSlotStartHour = (slot) => {
    const timeStr = slot.split('-')[0].trim();
    const [time, meridiem] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    if (meridiem === 'PM' && hours !== 12) hours += 12;
    if (meridiem === 'AM' && hours === 12) hours = 0;
    return hours + minutes / 60;
  };
  const isAfternoonSlot = (slot) => getSlotStartHour(slot) >= 13;

  const getAvailableTimeSlots = () => {
    if (!formData.date) return allTimeSlots;
    const now = new Date();
    const selectedDate = new Date(formData.date);
    const currentTimeInHours = now.getHours() + now.getMinutes() / 60;
    const isToday = selectedDate.toDateString() === now.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = selectedDate.toDateString() === tomorrow.toDateString();
    if (isToday) return allTimeSlots.filter(s => getSlotStartHour(s) >= currentTimeInHours + 4);
    if (isTomorrow && now.getHours() >= 20) return allTimeSlots.filter(s => isAfternoonSlot(s));
    return allTimeSlots;
  };

  const timeSlots = getAvailableTimeSlots();

  React.useEffect(() => {
    if (formData.date && formData.timeSlot) {
      const slots = getAvailableTimeSlots();
      if (!slots.includes(formData.timeSlot)) setFormData(p => ({ ...p, timeSlot: '' }));
    }
  }, [formData.date]);

  React.useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/vehicles`);
        const data = await response.json();
        const vehicles = data.vehicles || [];
        const brands = [...new Set(vehicles.map(v => v.brand))].sort();
        setAvailableBrands(brands);
      } catch (error) { /* keep fallback */ }
    };
    fetchVehicles();
  }, []);

  React.useEffect(() => {
    if (formData.carBrand) {
      const fetchModels = async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/vehicles`);
          const data = await response.json();
          const vehicles = data.vehicles || [];
          const models = vehicles.filter(v => v.brand === formData.carBrand).map(v => v.model).filter((m, i, s) => s.indexOf(m) === i).sort();
          setAvailableModels(models);
        } catch (error) {
          setAvailableModels(carModels[formData.carBrand] || []);
        }
      };
      fetchModels();
    }
  }, [formData.carBrand]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === 'email' && otpVerified) {
      setOtpVerified(false); setOtpSent(false); setOtp('');
    }
  };

  const handleSendOtp = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      toast({ title: 'Invalid Email', description: 'Please enter a valid email address', variant: 'destructive' });
      return;
    }
    setSendingOtp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      const data = await response.json();
      if (data.success) {
        setOtpSent(true); setOtpTimer(60);
        toast({ title: 'OTP Sent!', description: `Verification code sent to ${formData.email}` });
        const timer = setInterval(() => setOtpTimer(p => { if (p <= 1) { clearInterval(timer); return 0; } return p - 1; }), 1000);
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to send OTP', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to send OTP', variant: 'destructive' });
    } finally { setSendingOtp(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast({ title: 'Invalid OTP', description: 'Please enter a valid 6-digit OTP', variant: 'destructive' });
      return;
    }
    setVerifyingOtp(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, otp })
      });
      const data = await response.json();
      if (data.success && data.verified) {
        setOtpVerified(true);
        toast({ title: 'Verified', description: 'Email verified successfully' });
      } else {
        toast({ title: 'Invalid OTP', description: data.detail || 'Please check the OTP and try again', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Verification Failed', description: 'Please try again', variant: 'destructive' });
    } finally { setVerifyingOtp(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile || !formData.carBrand || !formData.carModel || !formData.location || !formData.date || !formData.timeSlot) {
      toast({ title: 'Incomplete Form', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    if (!otpVerified) {
      toast({ title: 'Email Not Verified', description: 'Please verify your email address with OTP', variant: 'destructive' });
      return;
    }
    try {
      setIsSubmitting(true);
      const bookingData = {
        name: formData.name, email: formData.email, mobile: formData.mobile,
        carBrand: formData.carBrand, carModel: formData.carModel,
        fuelType: formData.fuelType,
        manufacturingYear: formData.manufacturingYear ? parseInt(formData.manufacturingYear) : null,
        service, package: selectedPackage,
        date: format(formData.date, 'yyyy-MM-dd'),
        timeSlot: formData.timeSlot,
        location: formData.location, address: formData.address
      };
      const response = await bookingService.create(bookingData);
      if (response.success) {
        setBookingId(response.booking_id);
        setIsSubmitted(true);
        toast({ title: 'Success!', description: 'Your booking has been confirmed.' });
        setTimeout(() => navigate('/'), 3000);
      }
    } catch (error) {
      toast({ title: 'Booking Failed', description: error.detail || 'Failed to create booking. Please try again.', variant: 'destructive' });
    } finally { setIsSubmitting(false); }
  };

  if (isSubmitted) {
    return (
      <div data-testid="booking-confirmed" className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-slate-900/50 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-5">
            <CheckCircle className="w-8 h-8 text-cyan-400" />
          </div>
          <h2 className="text-2xl font-extrabold text-white mb-2 tracking-tight">Booking Confirmed</h2>
          <p className="text-slate-400 mb-5">Thank you for choosing Propitstop — we'll contact you shortly.</p>
          <div className="bg-slate-950/60 border border-slate-800 rounded-xl p-4 text-left space-y-1.5 mb-3">
            <p className="text-sm"><span className="text-slate-500">Booking ID</span> <span className="text-cyan-400 font-mono ml-2">{bookingId}</span></p>
            <p className="text-sm"><span className="text-slate-500">Service</span> <span className="text-white ml-2">{service}</span></p>
            <p className="text-sm"><span className="text-slate-500">Package</span> <span className="text-white ml-2">{selectedPackage?.name}</span></p>
            <p className="text-sm"><span className="text-slate-500">Date</span> <span className="text-white ml-2">{formData.date ? format(formData.date, 'PPP') : ''}</span></p>
            <p className="text-sm"><span className="text-slate-500">Time</span> <span className="text-white ml-2">{formData.timeSlot}</span></p>
          </div>
          <p className="text-xs text-slate-500">Please save your booking ID for future reference</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="min-h-screen bg-slate-950 text-slate-200">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 pt-24 pb-14">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(6,182,212,0.18),transparent_50%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs uppercase tracking-[0.2em] text-cyan-300 font-medium">Book Service</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white">
            Confirm your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">booking</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-xl">Fill in the details below — we'll handle the rest.</p>
        </div>
      </section>

      {/* Form */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 md:p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full Name *</label>
                      <Input data-testid="booking-name-input" type="text" placeholder="Enter your name"
                        value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Mobile Number *</label>
                      <Input data-testid="booking-mobile-input" type="tel" placeholder="Enter mobile number"
                        value={formData.mobile} onChange={handleInputChange} name="mobile" maxLength={10}
                        required className={inputCls} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Email *</label>
                    <div className="flex gap-2">
                      <Input data-testid="booking-email-input" type="email" placeholder="Enter email address"
                        value={formData.email} onChange={handleInputChange} name="email"
                        required disabled={otpVerified}
                        className={`flex-1 ${inputCls} disabled:opacity-70`} />
                      {!otpVerified && (
                        <Button type="button" onClick={handleSendOtp}
                          disabled={!formData.email || sendingOtp || (otpSent && otpTimer > 0)}
                          data-testid="send-otp-btn"
                          className="whitespace-nowrap bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">
                          {sendingOtp ? 'Sending...' : otpSent && otpTimer > 0 ? `Resend (${otpTimer}s)` : otpSent ? 'Resend OTP' : 'Send OTP'}
                        </Button>
                      )}
                      {otpVerified && (
                        <div className="flex items-center text-cyan-400 font-medium px-3 gap-2">
                          <CheckCircle className="w-5 h-5" /> Verified
                        </div>
                      )}
                    </div>

                    {otpSent && !otpVerified && (
                      <div className="mt-3 p-4 rounded-xl bg-slate-950/60 border border-cyan-500/20">
                        <p className="text-sm text-slate-300 mb-3">Enter the 6-digit OTP sent to <span className="text-cyan-400">{formData.email}</span></p>
                        <div className="flex gap-2">
                          <Input type="text" placeholder="Enter OTP" value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} maxLength={6}
                            data-testid="otp-input" className={`flex-1 ${inputCls} tracking-[0.3em] text-center font-mono`} />
                          <Button type="button" onClick={handleVerifyOtp}
                            disabled={!otp || otp.length !== 6 || verifyingOtp}
                            data-testid="verify-otp-btn"
                            className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-xl">
                            {verifyingOtp ? 'Verifying...' : 'Verify'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Car Brand *</label>
                      <Select value={formData.carBrand} onValueChange={(v) => setFormData({ ...formData, carBrand: v, carModel: '' })}>
                        <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select brand" /></SelectTrigger>
                        <SelectContent className={`${selectContentCls} max-h-[300px]`}>
                          {availableBrands.map(b => <SelectItem key={b} value={b} className={selectItemCls}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={labelCls}>Car Model *</label>
                      <Select value={formData.carModel} onValueChange={(v) => setFormData({ ...formData, carModel: v })} disabled={!formData.carBrand}>
                        <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select model" /></SelectTrigger>
                        <SelectContent className={`${selectContentCls} max-h-[300px]`}>
                          {availableModels.map(m => <SelectItem key={m} value={m} className={selectItemCls}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Fuel Type</label>
                      <Select value={formData.fuelType} onValueChange={(v) => setFormData({ ...formData, fuelType: v })}>
                        <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select fuel type" /></SelectTrigger>
                        <SelectContent className={selectContentCls}>
                          {['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid'].map(f => <SelectItem key={f} value={f} className={selectItemCls}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={labelCls}>Manufacturing Year</label>
                      <Input type="number" placeholder="e.g., 2020" value={formData.manufacturingYear}
                        onChange={(e) => setFormData({ ...formData, manufacturingYear: e.target.value })}
                        min="1990" max={new Date().getFullYear()} className={inputCls} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Location in Bangalore *</label>
                    <Select value={formData.location} onValueChange={(v) => setFormData({ ...formData, location: v })}>
                      <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select location" /></SelectTrigger>
                      <SelectContent className={selectContentCls}>
                        {bangaloreLocations.map(loc => <SelectItem key={loc} value={loc} className={selectItemCls}>{loc}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className={labelCls}>Pickup Address</label>
                    <Input type="text" placeholder="Enter complete address"
                      value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className={inputCls} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Preferred Date *</label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start text-left font-normal bg-slate-950/60 border-slate-700 text-white hover:bg-slate-900 hover:text-white hover:border-cyan-500/40">
                            <CalendarIcon className="mr-2 h-4 w-4 text-cyan-400" />
                            {formData.date ? format(formData.date, 'PPP') : <span className="text-slate-500">Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-slate-900 border-slate-700" align="start">
                          <Calendar mode="single" selected={formData.date}
                            onSelect={(date) => setFormData({ ...formData, date, timeSlot: '' })}
                            disabled={(date) => {
                              const today = new Date(); today.setHours(0, 0, 0, 0);
                              if (date < today) return true;
                              if (date.getDay() === 0) return true;
                              return false;
                            }}
                            className="text-white" />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <label className={labelCls}>Time Slot *</label>
                      {timeSlots.length > 0 ? (
                        <Select value={formData.timeSlot} onValueChange={(v) => setFormData({ ...formData, timeSlot: v })}>
                          <SelectTrigger className={selectTriggerCls}><SelectValue placeholder="Select time" /></SelectTrigger>
                          <SelectContent className={selectContentCls}>
                            {timeSlots.map(s => <SelectItem key={s} value={s} className={selectItemCls}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-sm text-yellow-300">
                          No time slots available for this date. Please select another.
                        </div>
                      )}
                    </div>
                  </div>

                  <Button type="submit" disabled={isSubmitting} data-testid="confirm-booking-btn"
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold py-6 text-base rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.25)] hover:shadow-[0_0_30px_rgba(6,182,212,0.45)] transition-[box-shadow,background-color] duration-300 disabled:opacity-60">
                    {isSubmitting ? 'Booking...' : 'Confirm Booking'}
                  </Button>
                </form>
              </div>
            </div>

            {/* Summary */}
            <div>
              <div className="sticky top-24 bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium mb-4">Booking Summary</h3>
                <div className="space-y-4">
                  <div className="pb-3 border-b border-slate-800">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Service</p>
                    <p className="font-semibold text-white mt-1">{service || 'Not selected'}</p>
                  </div>
                  <div className="pb-3 border-b border-slate-800">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Package</p>
                    <p className="font-semibold text-white mt-1">{selectedPackage?.name || 'Not selected'}</p>
                  </div>
                  <div className="pb-3 border-b border-slate-800">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Vehicle</p>
                    <p className="font-semibold text-white mt-1">
                      {formData.carBrand && formData.carModel ? `${formData.carBrand} ${formData.carModel}` : 'Not selected'}
                    </p>
                    {formData.fuelType && <p className="text-xs text-slate-400 mt-0.5">{formData.fuelType}{formData.manufacturingYear ? ` · ${formData.manufacturingYear}` : ''}</p>}
                  </div>
                  <div className="pb-3 border-b border-slate-800">
                    <p className="text-xs uppercase tracking-wider text-slate-500">Duration</p>
                    <p className="font-semibold text-white mt-1">{selectedPackage?.duration || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500">Total</p>
                    <p className="mt-1 text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 tracking-tight">
                      ₹{selectedPackage?.price || '0'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">*Final price may vary based on inspection</p>
                  </div>
                </div>

                <div className="mt-6 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 space-y-2">
                  {['Free Pickup & Drop', '6 Month Warranty', 'Genuine Parts'].map((perk) => (
                    <div key={perk} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="text-sm text-slate-300">{perk}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
